import React, { useState } from 'react';
import { Box,Card,  Typography, Divider, Paper, IconButton, Tooltip, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CardContent, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector, useDispatch } from 'react-redux';
import { selectAdminAuth, setAdminApiKey } from '../../store/slices/adminAuthSlice';
import AdminProfileAPITabs from '../Admin/AdminProfileAPITabs';
import { createAdminApiKey } from '../../api/admin';
import { toast } from 'react-toastify';

const offendersApiUrl = process.env.REACT_APP_OFFENDERS_API_URL || "https://api.offenderlist-api.com/offenders";
const queryParams = "?state=[state]&exact=[exact]&zip_code=[code]&first_name=[firstname]&longitude=[longitude]&radius=[radius]&last_name=[lastname]&dob=[dob]&latitude=[latitude]&offset=[offset]";
const endpoint = `${offendersApiUrl}${queryParams}`;
const responseBody = `[
    {
        "code": "string",
        "state": "2-char state code",
        "zip": "string",
        "latitude": "number",
        "longitude": "number",
        "first_name": "string",
        "middle_name": "string",
        "last_name": "string",
        "full_name": "string",
        "birthday": "YYYY-MM-DD",
        "age": number,
        "address_line1": "string",
        "address_line2": "string",
        "full_address": "string",
        "sex": "string",
        "race": "string",
        "height": "string",
        "weight": "number",
        "hair_color": "string",
        "eye_color": "string",
        "scars_marks": "string",
        "aliases": "string",
        "offense_details": "string",
        "other_addresses": "string",
        "is_predator": "boolean",
        "is_absconder": "boolean",
        "image_url": "string",
        "created_at": "YYYY-MM-DDTHH:MM:SS.000Z",
        "updated_at": "YYYY-MM-DDTHH:MM:SS.000Z",
        "synced_at": "YYYY-MM-DDTHH:MM:SS.000Z",
        "location": "string"
    },
    ...
]`;

const errorCases = [
  {
    code: 403,
    message: "FORBIDDEN: Access Denied (Missing API key!)",
    description: "API key was not provided in the request header."
  },
  {
    code: 403,
    message: "FORBIDDEN: Invalid API key",
    description: "The provided API key is invalid."
  },
  {
    code: 403,
    message: "FORBIDDEN: Invalid API key (Disabled by admin)",
    description: "The API key has been disabled by an administrator."
  },
  {
    code: 429,
    message: "MAX_REQUESTS_EXCEEDED: You have exceeded the maximum number of API requests available under your current billing plan.",
    description: "The request quota for your plan has been exceeded."
  },
  {
    code: 400,
    message: "PRIMARY_PARAMETER_REQUIRED: At least one primary parameter is required: last_name, zip_code, or latitude/longitude/radius.",
    description: "A required primary parameter is missing from the request."
  },
  {
    code: 400,
    message: "SECONDARY_PARAMETER_REQUIRED: At least one secondary parameter is required: dob, first_name, state, zip_code, or latitude/longitude/radius.",
    description: "A required secondary parameter is missing from the request."
  },
  {
    code: 500,
    message: "INTERNAL_SERVER_ERROR: An unexpected error occurred while processing your request.",
    description: "A server error occurred. Please try again later."
  }
];

const AdminAPIDocumentation = () => {
  const [copied, setCopied] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { token, apiKey } = useSelector(selectAdminAuth);

  const handleCopy = (label, text) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied("") , 3000);
  };

  const handleFetchApiKey = async () => {
    if (!token) {
      toast.error('Admin token missing. Please login again.');
      return;
    }
    setLoading(true);
    try {
      const res = await createAdminApiKey(token);
      if (res.api_key) {
        dispatch(setAdminApiKey(res.api_key));
        toast.success('API key created successfully!');
      } else {
        toast.error(res.message || 'Failed to create API key');
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: { xs: 1, sm: 2 } }}>
      <AdminProfileAPITabs selectedTab={1} />
      {/* <Typography variant="h4" fontWeight={700} gutterBottom>
        API Documentation
      </Typography> */}
      {/* <Typography variant="body1" gutterBottom>
        Manage your api key and authentication details to start using the Offenderlist API.
      </Typography> */}
      
      <Box sx={{ maxHeight: '80vh', overflow: 'auto', px: { xs: 0, sm: 1 }, py: 1 }}>
        <Card sx={{ my:2}}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1, mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        {/* Label Box */}
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'left',
            p: '6px 16px',
            fontFamily: 'monospace',
            fontSize: 15,
            background: '#f5f5f5',
            minWidth: 90,
            width: { xs: 90, sm: 120 },
            justifyContent: 'left',
            mr: 1
          }}
        >
          x-api-key
        </Paper>
        {/* Value Box */}
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: '2px 12px',
            fontFamily: 'monospace',
            fontSize: 15,
            background: '#f5f5f5',
            minWidth: { xs: 0, sm: 240 },
            width: { xs: '100%', sm: 600 },
            maxWidth: '100%',
            overflowX: 'auto',
            mr: 1
          }}
        >
          <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'auto', textOverflow: 'ellipsis' }}>
            {apiKey ? apiKey : 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
          </span>
          <Tooltip
            title={copied === 'apiKey' ? 'Copied!' : 'Copy'}
            open={Boolean(copied === 'apiKey')}
          >
            <span>
              <IconButton
                onClick={() => handleCopy('apiKey', apiKey ? apiKey : 'xxxxxxxxxx')}
                size="small"
                disabled={!apiKey}
              >
                {copied === 'apiKey'
                  ? <CheckCircleIcon color="success" fontSize="small" />
                  : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Paper>
        {/* Fetch Button */}
        <Button
          variant="contained"
          color="primary"
          disabled={loading || !!apiKey}
          onClick={handleFetchApiKey}
          sx={{ minWidth: 90, width: { xs: 90, sm: 120 }, height: 36, fontWeight: 600, fontSize: 13, borderRadius: 1, p: 0 }}
        >
          {loading ? 'Fetching...' : 'Fetch API Key'}
        </Button>
      </Box>
      {/* Tip above Offenders Search Endpoint */}
      <Box
        sx={{
          mt: 0.5,
          mb: 2,
          ml: 0.5,
          display: 'flex',
          alignItems: 'center',
          fontSize: 13,
          color: 'text.secondary'
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
        <span>
          Add this to the request header to all requests.
        </span>
      </Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Offenderlist Search Endpoint
      </Typography>
      {/* Stylish endpoint row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          overflowX: 'auto',
          width: '100%',
          background: '#f5f5f5',
          borderRadius: 1,
          p: 1.2,
          fontFamily: 'monospace',
          fontSize: { xs: 13, sm: 15 },
          boxShadow: 0,
          border: '1px solid #e0e0e0',
        }}
      >
        <span style={{ color: '#1976d2', fontWeight: 600, marginRight: 8 }}>GET:</span>
        <span style={{ color: '#333', whiteSpace: 'nowrap', fontWeight: 500 }}>{endpoint}</span>
        <Tooltip
          title={copied === 'endpoint' ? 'Copied!' : 'Copy Endpoint'}
          open={Boolean(copied === 'endpoint')}
        >
          <IconButton onClick={() => handleCopy('endpoint', endpoint)} size="small" sx={{ ml: 1 }}>
            {copied === 'endpoint'
              ? <CheckCircleIcon color="success" fontSize="small" />
              : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      {/* Tip below endpoint */}
      <Box
        sx={{
          mt: 0.5,
          mb: 2,
          ml: 0.5,
          display: 'flex',
          alignItems: 'center',
          fontSize: 13,
          color: 'text.secondary'
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5, color: 'primary.main' }} />
        <span>
          Tip: Enable or disable query parameters in Postman or your preferred testing tool to test different scenarios.
        </span>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mr: 1 }}>
          Query Parameters:
        </Typography>
      </Box>
      {/* Query Parameters Table */}
      <TableContainer component={Paper} sx={{ mb: 2, maxWidth: '100%', overflowX: 'auto', background: '#f5f5f5', }}>
        <Table size="small" aria-label="query parameters table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow><TableCell>state</TableCell><TableCell>string</TableCell><TableCell>Filter by state abbreviation (e.g., TX).</TableCell></TableRow>
            <TableRow><TableCell>zip_code</TableCell><TableCell>string</TableCell><TableCell>Filter by ZIP code.</TableCell></TableRow>
            <TableRow><TableCell>first_name</TableCell><TableCell>string</TableCell><TableCell>Filter by first name. Supports fuzzy/phonetic or exact match.</TableCell></TableRow>
            <TableRow><TableCell>last_name</TableCell><TableCell>string</TableCell><TableCell>Filter by last name. Supports fuzzy/phonetic or exact match.</TableCell></TableRow>
            <TableRow><TableCell>dob</TableCell><TableCell>date</TableCell><TableCell>Filter by date of birth (YYYY-MM-DD).</TableCell></TableRow>
            <TableRow><TableCell>exact</TableCell><TableCell>boolean</TableCell><TableCell>Set to true for exact name matching.</TableCell></TableRow>
            {/* <TableRow><TableCell>range</TableCell><TableCell>boolean</TableCell><TableCell>Enable DOB ±1-year matching. Works only with exact search mode. If <code>exact=false</code> but <code>range=true</code>, exact search will still be applied.</TableCell></TableRow>
            <TableRow><TableCell>alias</TableCell><TableCell>boolean</TableCell><TableCell>When <code>true</code>, the search will also include the <code>aliases</code> field for matching first and last names. This allows users to search using alternate names the person is known by. Works for both exact and fuzzy matching.</TableCell></TableRow> */}
            <TableRow><TableCell>longitude</TableCell><TableCell>number</TableCell><TableCell>Longitude for geographical filtering.</TableCell></TableRow>
            <TableRow><TableCell>latitude</TableCell><TableCell>number</TableCell><TableCell>Latitude for geographical filtering.</TableCell></TableRow>
            <TableRow><TableCell>radius</TableCell><TableCell>number</TableCell><TableCell>Search radius in miles (along with longitude and latitude)</TableCell></TableRow>
            <TableRow><TableCell>offset</TableCell><TableCell>number</TableCell><TableCell>Offset for pagination.</TableCell></TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Features Section */}
      <Card sx={{ mb: 4, borderRadius: 4, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Features</Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center">
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                Fuzzy and Phonetic Matching — <span style={{ fontWeight: 400 }}>Find approximate name matches</span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                Exact Matching — <span style={{ fontWeight: 400 }}>Add <b>exact=true</b> to force precise matches</span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                Geographical Filters — <span style={{ fontWeight: 400 }}>Filter offenders by location within a radius (longitude & latitude)</span>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                Pagination — <span style={{ fontWeight: 400 }}>Use <b>offset</b> to fetch paginated results</span>
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Example Requests Section */}
      <Card sx={{ mb: 4, borderRadius: 4, boxShadow: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Example Requests</Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            {[
              {
                label: 'Fuzzy Matching with Name',
                url: 'https://api.offenderlist-api.com/offenders?state=TX&last_name=Doe'
              },
              {
                label: 'Exact Matching',
                url: 'https://api.offenderlist-api.com/offenders?state=TX&exact=true&last_name=Doe'
              },
              {
                label: 'Geographical Query',
                url: 'https://api.offenderlist-api.com/offenders?longitude=-97.34326935&latitude=32.75904846&radius=999999&offset=11'
              },
              {
                label: 'Fuzzy Match with Age',
                url: 'https://api.offenderlist-api.com/offenders?first_name=JOHN&last_name=Doe&dob=1992-02-08',
                note: '(Returns potential matches for offenders with a first name similar to "John", a last name "Doe", and a date of birth in 1992 (e.g., offenders of same age).)'
              }
            ].map((ex, i) => (
              <Box key={i}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{ex.label}</Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    background: '#f5f7fa',
                    fontFamily: 'monospace',
                    fontSize: 15,
                    display: 'flex',
                    alignItems: 'center',
                    mb: ex.note ? 0.5 : 1,
                    overflowX: 'auto',
                    width: '100%',
                  }}
                >
                  <span style={{ flex: 1, whiteSpace: 'nowrap' }}><b>GET</b> {ex.url}</span>
                  <Tooltip
                    title={copied === `example-${i}` ? 'Copied!' : 'Copy to clipboard'}
                    open={Boolean(copied === `example-${i}`)}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(ex.url);
                          setCopied(`example-${i}`);
                          setTimeout(() => setCopied(""), 3000);
                        }}
                      >
                        {copied === `example-${i}` ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Paper>
                {ex.note && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {ex.note}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Return body (Success):
      </Typography>
      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, background: '#272822', color: '#fff', fontFamily: 'monospace', fontSize: 14, mb: 2, overflowX: 'auto', width: '100%' }}>
        <pre style={{ margin: 0, whiteSpace: 'pre', overflowX: 'auto' }}>{responseBody}</pre>
      </Paper>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Error Responses:
            </Typography>
            <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto', background: '#f5f5f5' }}>
              <Table size="small" aria-label="error responses table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, minWidth: 120, whiteSpace: 'nowrap' }}>Status Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Message</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorCases.map((err, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{err.code}</TableCell>
                      <TableCell style={{ fontFamily: 'monospace' }}>{err.message}</TableCell>
                      <TableCell>{err.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminAPIDocumentation;